import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquareText, Trash2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { get } from "@/lib/api";
import { relDays } from "@/lib/format";
import { useAssistant } from "@/context/AssistantContext";

/** Conversation history for the signed-in user. */
export const Sessions = ({ className, onPick }) => {
  const { sessionId, loadSession, deleteSession, newSession } = useAssistant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ai-sessions"], queryFn: () => get("/ai/sessions"), staleTime: 10_000 });
  const pick = async (id) => {
    await loadSession(id);
    onPick?.();
  };
  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteSession(id);
    qc.invalidateQueries({ queryKey: ["ai-sessions"] });
  };
  return (
    <div className={cn("flex min-h-0 flex-col", className)} data-testid="ai-sessions">
      <div className="flex items-center justify-between px-3 pb-2 pt-3">
        <span className="text-[11px] font-medium text-muted-foreground">Samtaler</span>
        <button
          type="button"
          onClick={() => {
            newSession();
            onPick?.();
          }}
          className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[11.5px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
          data-testid="ai-sessions-new"
        >
          <Plus className="h-3.5 w-3.5" /> Ny
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin">
        {q.isLoading && <div className="px-2 py-3 text-[12px] text-muted-foreground">Laster …</div>}
        {q.data?.length === 0 && <div className="px-2 py-3 text-[12px] text-muted-foreground">Ingen tidligere samtaler.</div>}
        {q.data?.map((s) => (
          <div key={s.id} role="button" tabIndex={0} onClick={() => pick(s.id)} onKeyDown={(e) => e.key === "Enter" && pick(s.id)} className={cn("group flex cursor-pointer items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-muted/70", s.id === sessionId && "bg-muted")} data-testid="ai-session-item">
            <MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] leading-5">{s.title}</span>
              <span className="block truncate text-[11px] leading-4 text-muted-foreground">
                {relDays(s.updated_at?.slice(0, 10))} · {s.message_count} meldinger
              </span>
            </span>
            <button type="button" onClick={(e) => remove(e, s.id)} className="mt-0.5 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity duration-150 hover:bg-card hover:text-rose-600 group-hover:opacity-100 focus-visible:opacity-100" aria-label="Slett samtale" data-testid="ai-session-delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
